import { useEffect, useState } from 'react'
import { Link, useNavigate, useParams } from 'react-router-dom'
import { ForumSeo } from '@/components/ForumSeo'
import { RichEditor } from '@/components/richtext/RichEditor'
import { Button } from '@/components/ui/button'
import { apiFetch } from '@/lib/api'
import { IconLoader2 } from '@tabler/icons-react'
import { useAuthLoading, useConfirmedGuest, useSessionUser } from '@/lib/auth'
import {
  forumAlertError,
  forumAlertWarn,
  forumCard,
  forumCardHeader,
  forumKicker,
  forumLink,
  forumMutedSm,
  forumSectionBar,
  forumLoaderRow,
  forumTitleMd,
} from '@/lib/forumUi'
import { forumPageTitle, truncateForMetaDescription } from '~shared/seo'

export function EditPostPage() {
  const { postId } = useParams<{ postId: string }>()
  const navigate = useNavigate()
  const me = useSessionUser()
  const authLoading = useAuthLoading()
  const guest = useConfirmedGuest()

  const [slug, setSlug] = useState<string | null>(null)
  const [content, setContent] = useState<unknown>({ type: 'doc', content: [{ type: 'paragraph' }] })
  const [canEdit, setCanEdit] = useState(false)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    if (!postId) return
    let alive = true

    void apiFetch<{
      post: {
        threadSlug: string
        content: unknown
        permissions: { canEdit: boolean }
      }
    }>(`/api/posts/${encodeURIComponent(postId)}`)
      .then(payload => {
        if (!alive) return
        setSlug(payload.post.threadSlug)
        setContent(payload.post.content)
        setCanEdit(payload.post.permissions.canEdit)
      })
      .catch(() => {
        if (!alive) return
        setError('Post not found or editing is unavailable.')
      })

    return () => {
      alive = false
    }
  }, [postId])

  if (authLoading) {
    return (
      <div className={forumLoaderRow} aria-busy="true" aria-live="polite">
        <IconLoader2 className="size-5 shrink-0 animate-spin" aria-hidden="true" />
        Checking your session…
      </div>
    )
  }

  if (guest) {
    return (
      <section className={`${forumCard} px-5 py-10 md:px-6`}>
        <h1 className={forumTitleMd}>Edit post</h1>
        <p className={`${forumMutedSm} mt-2 text-zinc-600`}>Sign in to edit.</p>
        <Link className="mt-5 inline-block" to={`/sign-in?next=/posts/${postId ?? ''}/edit`}>
          <Button>Sign in</Button>
        </Link>
      </section>
    )
  }

  if (!me) return null

  return (
    <div className="space-y-6">
      <ForumSeo
        title={forumPageTitle('Edit reply')}
        description={truncateForMetaDescription('Update one of your posts on NK Our Town.')}
        noindex
      />
      <header className={forumCard}>
        <div className={forumCardHeader}>
          <p className={forumKicker}>Edit</p>
          <h1 className={forumTitleMd}>Edit reply</h1>
          {slug ? (
            <p className="mt-3 text-[14px]">
              <Link className={forumLink} to={`/t/${slug}`}>
                ← Back to thread
              </Link>
            </p>
          ) : null}
        </div>
      </header>

      {error ? <div role="alert" className={forumAlertError}>{error}</div> : null}

      {!canEdit ? (
        <div role="status" className={forumAlertWarn}>
          This post cannot be edited (locked thread or moderator action).
        </div>
      ) : null}

      <div className={forumCard}>
        <div className={forumSectionBar}>
          <span className="text-xs font-semibold uppercase tracking-wider text-zinc-600">Content</span>
        </div>
        <div className="border-t border-zinc-100 px-5 py-4 md:px-6">
          <RichEditor initialJSON={content} onChangeJSON={setContent} />
        </div>
      </div>

      <div className="flex flex-wrap gap-3 overflow-hidden rounded-2xl border border-zinc-200 bg-white px-5 py-4 shadow-sm shadow-zinc-950/5 md:px-6">
        <Button
          type="button"
          disabled={!canEdit || !postId}
          onClick={async () => {
            try {
              await apiFetch(`/api/posts/${encodeURIComponent(postId!)}`, {
                method: 'PATCH',
                json: { content },
              })
              if (slug) navigate(`/t/${slug}`)
            } catch {
              setError('Save failed. Links must use HTTPS.')
            }
          }}
        >
          Save changes
        </Button>
      </div>
    </div>
  )
}
